import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import Bugsnag from '@bugsnag/js';
import { css } from '@emotion/react';
import DoubleArrowRoundedIcon from '@mui/icons-material/DoubleArrowRounded';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createCheckAutoReconnectOutbound } from '../../api/outbound/entryOutbounds';
import { isConfigFlagEnabled } from '../../config';
import { FAKE_LOCAL_TESTING_PASSWORD } from '../../constants/constants';
import { useInstanceId } from '../../context/instance/instanceContext';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { switchToSignUp, thunkStartLogin } from '../../redux/actions/gameEntryActions';
import { addMessageBox } from '../../redux/actions/messageBoxesActions';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { Form } from '../../styles/styles';
import { getAutoReconnectSessionUuid } from '../../utils/autoReconnectUtils';
import { addAsyncBugsnagMetadata } from '../../utils/bugsnagUtils';
import { getAutoLoginPrefixFromUrl, getPrefillPasswordFromUrl, getPrefillUsernameFromUrl, isLocalhostEnvironment, isTestEnvironment } from '../../utils/utils';
import { getIsWebGlSupportedViaPixiUtil, isWebGlWarningForced } from '../../utils/webGlUtils';
import { EntryPanel } from './entryPanel';
import { ForgottenLoginControl } from './forgottenLoginControl';
import { WebGlUnsupportedWarningContainer } from './webGlUnsupportedWarningContainer';
function getDefaultLoginUsername(instanceId) {
    const autoLoginPrefix = getAutoLoginPrefixFromUrl();
    if (autoLoginPrefix) {
        return `${autoLoginPrefix}${instanceId + 1}`;
    }
    const prefillUsername = getPrefillUsernameFromUrl(instanceId);
    if (prefillUsername) {
        return prefillUsername;
    }
    if (isTestEnvironment() || !isLocalhostEnvironment()) {
        // don't pre-fill
        return '';
    }
    return `p${instanceId + 1}`;
}
function getDefaultLoginPassword(instanceId) {
    const autoLoginPrefix = getAutoLoginPrefixFromUrl();
    if (autoLoginPrefix) {
        return FAKE_LOCAL_TESTING_PASSWORD;
    }
    const prefillPassword = getPrefillPasswordFromUrl(instanceId);
    if (prefillPassword) {
        return prefillPassword;
    }
    if (isTestEnvironment() || !isLocalhostEnvironment()) {
        // don't pre-fill
        return '';
    }
    return FAKE_LOCAL_TESTING_PASSWORD;
}
export const LoginPanel = () => {
    const isActionPending = useAppSelector((state) => state.gameEntry.isActionPending);
    const isWebGlSupported = useMemo(() => (isWebGlWarningForced() ? false : getIsWebGlSupportedViaPixiUtil()), []);
    const socketSend = useSocketSend();
    const instanceId = useInstanceId();
    const [username, setUsername] = useState(getDefaultLoginUsername(instanceId));
    const [password, setPassword] = useState(getDefaultLoginPassword(instanceId));
    const dispatch = useAppDispatch();
    const onChangeUsername = useCallback((event) => {
        setUsername(event.target.value);
    }, []);
    const onChangePassword = useCallback((event) => {
        setPassword(event.target.value);
    }, []);
    const loginNow = useCallback(() => {
        dispatch(thunkStartLogin(socketSend, username, password));
    }, [dispatch, password, socketSend, username]);
    const doLogin = useCallback(() => {
        if (isWebGlSupported) {
            loginNow();
        }
        else {
            Bugsnag.addMetadata('session', { webGlLoginWarningShown: true });
            dispatch(addMessageBox({ type: 'web-gl-login-warning', username, password }));
        }
        addAsyncBugsnagMetadata();
    }, [dispatch, isWebGlSupported, loginNow, password, username]);
    const onClickLogin = useCallback((event) => {
        event.preventDefault(); // prevents reloading the page
        doLogin();
    }, [doLogin]);
    const onClickSignUp = useCallback(() => {
        dispatch(switchToSignUp());
    }, [dispatch]);
    useEffect(() => {
        if (getAutoLoginPrefixFromUrl()) {
            // log in immediately
            doLogin();
        }
    }, [doLogin]);
    useEffect(() => {
        if (!isConfigFlagEnabled('autoReconnect')) {
            return;
        }
        const autoReconnectSessionUuid = getAutoReconnectSessionUuid();
        if (autoReconnectSessionUuid) {
            socketSend(createCheckAutoReconnectOutbound(autoReconnectSessionUuid));
        }
    }, [socketSend]);
    return (_jsxs(EntryPanel, { title: 'Play Now', ariaLabel: 'login panel', children: [_jsxs(Form, { autoComplete: 'on', children: [_jsx(TextField, { id: `login-username-${instanceId}`, type: 'text', variant: 'filled', fullWidth: true, label: 'Username', value: username, onChange: onChangeUsername, InputLabelProps: { shrink: true } }), _jsx(TextField, { id: `login-password-${instanceId}`, type: 'password', variant: 'filled', fullWidth: true, label: 'Password', value: password, onChange: onChangePassword, InputLabelProps: { shrink: true } }), _jsx(Button, { onClick: onClickLogin, disabled: isActionPending, variant: 'contained', endIcon: isActionPending ? _jsx(CircularProgress, { size: 20, color: 'inherit' }) : _jsx(DoubleArrowRoundedIcon, {}), color: 'secondary', type: 'submit' // may help to invoke 'save password' prompts
                        , children: "Login" }), _jsx(WebGlUnsupportedWarningContainer, {})] }), _jsxs("div", { css: css `
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;
        `, children: [_jsx(Typography, { variant: 'subtitle1', component: 'div', children: "New player?" }), _jsx("div", { css: css `
            padding: 5px;
          `, children: _jsx(Button, { onClick: onClickSignUp, disabled: isActionPending, variant: 'contained', children: "Sign Up" }) })] }), _jsx(ForgottenLoginControl, {})] }));
};
